/*
 * The work order list's from–to date filter, apart from the pickers that set it.
 *
 * Pure, in the shape of workOrderSearch.js, so which orders fall inside a range can be tested
 * without rendering a picker.
 *
 * Client-side, like the search: the list endpoint is not paged and returns all three dates, and
 * so does /mine. The range narrows what is shown, not what is fetched.
 */
import dayjs from "dayjs";
import { toValidDate } from "../cmms/cmmsFormat";

/* The three dates a range can be laid over, by the row field each one reads. */
export const WorkOrderDateField = {
  PlannedStart: "plannedStartDate",
  PlannedEnd: "plannedEndDate",
  Created: "createdAt",
};

const knownFields = Object.values(WorkOrderDateField);

/*
 * A picker's value as a day, or null when it names none.
 *
 * The pickers hand back a Dayjs, an Invalid Date Dayjs while a date is half typed, or null when
 * cleared. A half-typed date is no bound rather than a bound nothing passes, so the list does not
 * empty itself between keystrokes. Checked before dayjs() because dayjs(undefined) is now.
 */
const toDay = (value) => {
  if (value === null || value === undefined || value === "") return null;
  const day = dayjs(value);
  return day.isValid() ? day : null;
};

/*
 * The range as instants: the first moment of the Från day and the last of the Till day.
 *
 * Whole days in the browser's own time, both ends inclusive, as the week view counts its days:
 * an order raised at 00:30 on a Swedish 5 October was raised on the 5th, though it is still the
 * 4th in UTC. The time a picker happens to carry is dropped, so Till the 7th keeps the 7th's
 * afternoon.
 */
const getBounds = ({ from, to } = {}) => ({
  start: toDay(from)?.startOf("day").valueOf() ?? null,
  end: toDay(to)?.endOf("day").valueOf() ?? null,
});

/* Whether either end is set — what decides "Rensa filter" and the filtered empty state. */
export const isDateRangeActive = (range) => {
  const { start, end } = getBounds(range);
  return start !== null || end !== null;
};

/*
 * Från after Till, which no order can fall inside. Not corrected to the other order: the Till
 * picker says so, and the list stays empty until it is fixed.
 */
export const isDateRangeReversed = (range) => {
  const { start, end } = getBounds(range);
  return start !== null && end !== null && start > end;
};

/*
 * The orders whose chosen date falls inside the range.
 *
 * An order without that date is left out, a sweep-raised preventive order with no planned start
 * among them. The week view places such an order on its createdAt instead; this does not, because
 * a filter on Planerad start that let in orders with no planned start would not be one.
 *
 * No range returns the same array, not a copy, as filterWorkOrders does. Neither does a field this
 * module does not know: the select only offers the three, so that is a slip in the caller, and an
 * empty list would read as "no orders in this range".
 */
export const filterWorkOrdersByDate = (workOrders, { field, from, to } = {}) => {
  const rows = workOrders ?? [];
  const { start, end } = getBounds({ from, to });
  if ((start === null && end === null) || !knownFields.includes(field)) return rows;

  return rows.filter((row) => {
    const time = toValidDate(row?.[field])?.getTime();
    if (time === undefined) return false;
    return (start === null || time >= start) && (end === null || time <= end);
  });
};
